import React, { useState, useEffect } from 'react';
import AddDriverModal from '../components/AddDriverModal';
import UpdateDriverModal from '../components/UpdateDriverModal';
import ConfirmationModal from '../components/ConfirmationModal';
import ToastContainer from '../components/ToastContainer';
import { apiService, Driver } from '../services/api';
import { exportDriversToCSV } from '../utils/csvExport';
import { Search, Download, ChevronLeft, ChevronRight, Edit, Trash2 } from 'lucide-react';

interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalDrivers: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const ManageDrivers: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [driverToDelete, setDriverToDelete] = useState<string | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  
  // Pagination and search states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState<PaginationInfo>({
    currentPage: 1,
    totalPages: 1,
    totalDrivers: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [selectedDrivers, setSelectedDrivers] = useState<Set<string>>(new Set());

  const loadDrivers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getDrivers({
        page: currentPage,
        limit: limit,
        search: searchQuery || undefined,
      });
      
      console.log('ManageDrivers - Drivers response:', response);
      
      if (response.success && response.data) {
        const serverResponse = response.data as any;
        
        // Handle response structure: { success, message, data: [...], pagination: {...} }
        let driversArray: Driver[] = [];
        let paginationData: PaginationInfo | null = null;
        
        // Check if serverResponse.data exists (drivers array)
        if (serverResponse.data && Array.isArray(serverResponse.data)) {
          driversArray = serverResponse.data;
          paginationData = serverResponse.pagination;
        } else if (Array.isArray(serverResponse)) {
          // Fallback: if response.data is directly an array
          driversArray = serverResponse;
        }
        
        // Handle pagination
        if (serverResponse.pagination) {
          paginationData = serverResponse.pagination;
        } else if (response.pagination) {
          paginationData = response.pagination as any;
        }
        
        console.log('ManageDrivers - Drivers array:', driversArray);
        console.log('ManageDrivers - Pagination:', paginationData);
        
        setDrivers(driversArray);
        if (paginationData) {
          setPagination(paginationData);
        } else {
          // Default pagination if not provided
          setPagination({
            currentPage: currentPage,
            totalPages: Math.ceil(driversArray.length / limit),
            totalDrivers: driversArray.length,
            hasNextPage: false,
            hasPrevPage: currentPage > 1,
          });
        }
      } else {
        setError(response.error || 'Failed to load drivers');
        setDrivers([]);
      }
    } catch (error) {
      console.error('ManageDrivers - Error loading drivers:', error);
      setError('Failed to load drivers');
      setDrivers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery]);

  const handleAddDriver = (newDriver: Driver) => {
    console.log('ManageDrivers - New driver added:', newDriver);
    addToast('Driver added successfully', 'success');
    loadDrivers();
  };

  const handleUpdateDriver = (updatedDriver: Driver) => {
    console.log('ManageDrivers - Driver updated:', updatedDriver);
    addToast('Driver updated successfully', 'success');
    loadDrivers();
  };

  const handleEditDriver = (driver: Driver) => {
    setSelectedDriver(driver);
    setIsUpdateModalOpen(true);
  };

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleDeleteClick = (id: string) => {
    setDriverToDelete(id);
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (isBulkDelete) {
      // Bulk delete
      const driverIds = Array.from(selectedDrivers);
      if (driverIds.length === 0) {
        setShowConfirmModal(false);
        setIsBulkDelete(false);
        return;
      }

      try {
        const response = await apiService.bulkDeleteDrivers(driverIds);
        if (response.success) {
          const deletedCount = response.data?.deletedCount || driverIds.length;
          addToast(`${deletedCount} driver(s) deleted successfully`, 'success');
          setSelectedDrivers(new Set());
          loadDrivers();
        } else {
          addToast(response.error || 'Failed to delete drivers', 'error');
        }
      } catch (error) {
        addToast('An unexpected error occurred while deleting drivers', 'error');
      } finally {
        setShowConfirmModal(false);
        setIsBulkDelete(false);
      }
    } else {
      // Single delete
      if (!driverToDelete) return;

      try {
        const response = await apiService.deleteDriver(driverToDelete);
        if (response.success) {
          addToast('Driver deleted successfully', 'success');
          loadDrivers();
        } else {
          addToast(response.error || 'Failed to delete driver', 'error');
        }
      } catch (error) {
        addToast('An unexpected error occurred while deleting the driver', 'error');
      } finally {
        setShowConfirmModal(false);
        setDriverToDelete(null);
      }
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedDrivers.size === 0) {
      addToast('Please select at least one driver to delete', 'warning');
      return;
    }
    setIsBulkDelete(true);
    setShowConfirmModal(true);
  };

  const handleDeleteCancel = () => {
    setShowConfirmModal(false);
    setDriverToDelete(null);
    setIsBulkDelete(false);
  };

  const handleExportDrivers = () => {
    if (drivers.length === 0) {
      addToast('No drivers to export', 'warning');
      return;
    }
    exportDriversToCSV(drivers);
    addToast('Drivers exported successfully', 'success');
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = drivers.map(d => d._id).filter(Boolean) as string[];
      setSelectedDrivers(new Set(allIds));
    } else {
      setSelectedDrivers(new Set());
    }
  };

  const handleSelectDriver = (driverId: string) => {
    setSelectedDrivers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(driverId)) {
        newSet.delete(driverId);
      } else {
        newSet.add(driverId);
      }
      return newSet;
    });
  };

  const isAllSelected = drivers.length > 0 && drivers.every(d => d._id && selectedDrivers.has(d._id));

  // Format phone number for display
  const formatPhoneNumber = (phone?: string) => {
    if (!phone) return 'N/A';
    // If phone starts with 91, add +91 prefix
    if (phone.startsWith('91') && phone.length === 12) {
      return `+91 ${phone.slice(2, 7)} ${phone.slice(7)}`;
    }
    // If phone starts with +91, format it
    if (phone.startsWith('+91')) {
      const cleaned = phone.replace('+91', '').trim();
      if (cleaned.length === 10) {
        return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
      }
      return phone;
    }
    return phone;
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-full">
      {/* Page Title and Description */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Manage Driver</h1>
        <p className="text-gray-600">View, manage, and track driver performance and availability.</p>
      </div>

      {/* Search and Action Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search Bar */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by driver name, phone, email"
            value={searchQuery}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-jd-green focus:border-jd-green"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {selectedDrivers.size > 0 && (
            <button
              onClick={handleBulkDeleteClick}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected ({selectedDrivers.size})
            </button>
          )}
          {/* <button
            onClick={() => {}}
            className="inline-flex items-center px-4 py-2 border border-jd-green text-jd-green rounded-lg hover:bg-jd-green hover:text-white transition-colors"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </button> */}
          <button
            onClick={handleExportDrivers}
            className="inline-flex items-center px-4 py-2 border border-jd-green text-jd-green rounded-lg hover:bg-jd-green hover:text-white transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="inline-flex items-center px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <span className="mr-2">+</span>
            Add Driver
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-gray-500">Loading drivers...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <button
            onClick={loadDrivers}
            className="px-4 py-2 bg-jd-green text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Driver Table */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {!Array.isArray(drivers) || drivers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No drivers found. Add your first driver to get started.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-jd-green border-gray-300 rounded focus:ring-jd-green"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Driving License No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Address
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {drivers.map((driver, index) => {
                      const isSelected = driver._id ? selectedDrivers.has(driver._id) : false;
                      const driverName = driver.driverName || driver.name || 'N/A';
                      const phoneNumber = formatPhoneNumber(driver.mobileNumber || driver.phoneNumber);
                      const drivingLicense = driver.drivingLicense || driver.licenseNumber || 'N/A';
                      const address = driver.address || 'N/A';
                      const status = driver.status || 'Active';
                      
                      return (
                        <tr key={driver._id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => driver._id && handleSelectDriver(driver._id)}
                              className="w-4 h-4 text-jd-green border-gray-300 rounded focus:ring-jd-green"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {driverName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {phoneNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {drivingLicense}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {address}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                              status === 'Active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleEditDriver(driver)}
                                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => driver._id && handleDeleteClick(driver._id)}
                                className="text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className="px-3 sm:px-6 py-4 border-t border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between min-w-0">
                  <div className="text-xs sm:text-sm text-gray-700 shrink-0">
                    Showing <span className="font-medium">{(currentPage - 1) * limit + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * limit, pagination.totalDrivers)}
                    </span>{' '}
                    of <span className="font-medium">{pagination.totalDrivers}</span> drivers
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-1.5 sm:gap-2 w-full sm:w-auto min-w-0 flex-wrap">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center shrink-0"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline ml-0.5">Previous</span>
                    </button>
                    <div className="flex items-center gap-1 flex-wrap justify-center min-w-0">
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => {
                        // Show first page, last page, current page, and pages around current
                        if (
                          page === 1 ||
                          page === pagination.totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`min-w-[2rem] px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-lg shrink-0 ${
                                page === currentPage
                                  ? 'bg-jd-green text-white'
                                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return <span key={page} className="px-1 sm:px-2 text-gray-500 shrink-0">...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center shrink-0"
                    >
                      <span className="hidden sm:inline mr-0.5">Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Add Driver Modal */}
      <AddDriverModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddDriver={handleAddDriver}
      />

      <UpdateDriverModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedDriver(null);
        }}
        onUpdateDriver={handleUpdateDriver}
        driver={selectedDriver}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={isBulkDelete ? "Delete Selected Drivers" : "Delete Driver"}
        message={isBulkDelete 
          ? `Are you sure you want to delete ${selectedDrivers.size} selected driver(s)? This action cannot be undone.`
          : "Are you sure you want to delete this driver? This action cannot be undone."}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Toast Container */}
      <ToastContainer
        toasts={toasts}
        onRemoveToast={removeToast}
      />
    </div>
  );
};

export default ManageDrivers;
